import type { XeroDocumentLineAmountType } from '@/lib/types';

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * The vocabulary a QUOTE form and an INVOICE form share.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A quote and the invoice it converts into are the same document twice. Every
 * constant below was byte-identical in `QuoteFormModal` and `InvoiceFormModal`
 * — and then, on 2026-08-26, two parallel rewrites made
 * `DOCUMENT_LINE_AMOUNT_OPTIONS` NOT identical: the same three-member enum
 * ended up labelled two different ways on the two halves of one conversion.
 * That is a customer-facing tax treatment, so this module exists to make a
 * second copy impossible rather than merely to save lines.
 *
 * ⚠️ **Do not re-inline any of this into a form.** Three copies of a rule agree
 * only until one of them is edited, and the one that drifts is always the one
 * nobody was looking at. Same standing rule as `src/lib/inclusions.ts`.
 *
 * The disclosure and the two icons these forms also share live beside this file
 * in `DocumentFormBits.tsx`. They are split off for one mechanical reason: a
 * module that exports both components and constants breaks React Fast Refresh,
 * which is what `react(only-export-components)` warns about. Same folder, same
 * module in every sense that matters.
 */

/**
 * The three values a **DOCUMENT** accepts for its line-amount type.
 *
 * ⚠️ **This is NOT `LINE_AMOUNT_OPTIONS` from `@/lib/xero-tax-rates`, and
 * importing that one here would be a 422 on every save that used it.** A
 * company's `xeroSalesLineAmountType` is the CONTACT enum, whose third member
 * is `NONE`; a quote's or an invoice's is the DOCUMENT enum, whose third member
 * is `NOTAX`. Different WORD, not different casing — see
 * `XeroDocumentLineAmountType`. The server translates between the two when it
 * pushes, and it is the only place that should.
 *
 * `''` is not a fourth member. It is the ABSENCE of a value, stored as `null`,
 * which means "fall back to the company's Xero sales default" — never "no tax",
 * which is what `NOTAX` says. `USE_COMPANY_DEFAULT` is the wording for it.
 *
 * The labels say what XERO should do with the figures. They must never be read
 * as how EMERGE calculates: EMERGE's arithmetic is tax-exclusive with no
 * inclusive mode at all, whichever of these is chosen.
 *
 * ⚠️ **These three strings are short on purpose, and lengthening them breaks
 * three things at once.** A longer, explaining set was tried on the invoice
 * form ("Tax exclusive — tax added on top", and so on) and was rejected:
 *
 *  - **They are quoted inside prose.** The quote form renders `Xero will print
 *    this quote as "…"`, and `XeroSalesDefaultsFields` does the same for the
 *    contact enum. A label with an em dash and a clause after it stops being a
 *    noun phrase, and the explanation reads as part of the Xero setting's name.
 *  - **`Select` is a native `<select>`, which TRUNCATES rather than wraps.** At
 *    320px the trigger has roughly 246px of text room at 13px, so an explaining
 *    label loses its explanation exactly where it was supposed to teach — and
 *    the closed control is what a user reads nine times in ten.
 *  - **The contact enum next door already uses these three words**
 *    (`CompanyDetailPage`, `xero-tax-rates.ts`), and to a user the two enums
 *    ARE one concept — the wire-format difference is the server's business.
 *    Two spellings of one idea across a company and the quote raised against it
 *    is the drift this module was created to stop.
 *
 * The explaining belongs in the `Field` hint, where it is one sentence about
 * the choice rather than the same clause repeated three times in a list.
 */
export const DOCUMENT_LINE_AMOUNT_OPTIONS: readonly {
  value: XeroDocumentLineAmountType;
  label: string;
}[] = [
  { value: 'EXCLUSIVE', label: 'Tax exclusive' },
  { value: 'INCLUSIVE', label: 'Tax inclusive' },
  { value: 'NOTAX', label: 'No tax' },
];

/**
 * Narrows a stored string back to the union, or to `null`.
 *
 * Use this rather than `value as XeroDocumentLineAmountType`. The control can
 * only produce the three DOCUMENT members, but the baseline comes from the
 * server, and a value outside the union must never be smuggled into a write
 * claiming to be one. Anything unrecognised — including the contact enum's
 * `NONE` — becomes `null`, which says "fall back to the company's Xero sales
 * default", and never "no tax".
 */
export function toDocumentLineAmountType(value: string): XeroDocumentLineAmountType | null {
  return DOCUMENT_LINE_AMOUNT_OPTIONS.some((option) => option.value === value)
    ? (value as XeroDocumentLineAmountType)
    : null;
}

/** The chosen line-amount type as a label, or `undefined` when nothing is chosen. */
export function documentLineAmountLabel(value: string): string | undefined {
  return DOCUMENT_LINE_AMOUNT_OPTIONS.find((option) => option.value === value)?.label;
}

/** What a blank line-amount box means. Never "no tax" — that is `NOTAX`. */
export const USE_COMPANY_DEFAULT = "Use the company's Xero default";

/*
 * The money and currency shapes are NOT declared here any more.
 *
 * A deal obeys both of them too, and a deal is not one of the two forms this
 * module is the vocabulary for — so they had reached three copies
 * (`DealFormModal`, here, `InlineDealFields`) before being lifted to
 * `@/lib/document-validation`, which is the one definition now.
 *
 * They are re-exported rather than moved outright so every existing
 * `from '@/components/documents/document-form'` import keeps working: there is
 * still exactly one regex behind each name. ⚠️ Do not re-declare either here.
 *
 * `AMOUNT_PATTERN` is all four totals AND a deal's amount:
 * `^\d{1,12}(\.\d{1,2})?$`. `CURRENCY_PATTERN` is `^[A-Z]{3}$`, checked exactly
 * — "usd" is a 422, the server does not fold case.
 */
export { AMOUNT_PATTERN, CURRENCY_PATTERN } from '@/lib/document-validation';

/** The four stored money columns, identical on a quote and on an invoice. */
export const TOTAL_KEYS = ['subTotal', 'discountTotal', 'taxTotal', 'grandTotal'] as const;

export type TotalKey = (typeof TOTAL_KEYS)[number];

/*
 * Two of the four totals cannot be cleared, and the way they fail is quiet.
 *
 * `sub_total` and `grand_total` are `NOT NULL DEFAULT 0.00` on both tables, so
 * sending `null` passes zod and then takes the column-default path: the figure
 * is silently rewritten to 0.00 rather than rejected. On an imported record
 * that is the only copy of a real number — and `convert` copies these four onto
 * the invoice exactly as they stand — so an emptied box is refused client-side
 * instead: the user is told to enter a figure or put the old one back.
 *
 * `discountTotal` and `taxTotal` are genuinely nullable, and null on every
 * import, where it means "not recorded" and never zero. Clearing those is
 * honest and is allowed.
 */
export const UNCLEARABLE_TOTALS: readonly TotalKey[] = ['subTotal', 'grandTotal'];

export const TOTAL_LABELS: Record<TotalKey, string> = {
  subTotal: 'Sub-total',
  discountTotal: 'Discount total',
  taxTotal: 'Tax total',
  grandTotal: 'Grand total',
};
